import moment from "moment";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useQuery } from "react-query";
import { getCentroCusto } from "../../../../repositories/qerpapi/erp/pub/CentroCustoRepository";
import { findCashFlow } from "../../../../repositories/qerpapi/erp/relatorio/FluxoDeCaixaRepository";
import { findCompaniesV3 } from "../../../../repositories/qerpapi/v3/EmpresaRepository";
import { notify } from "../../../../utils/notify";
import {
  initialFilters,
  orientationLabelOptions,
  orientationOptions,
  periods,
  reportTypes,
} from "../../constants";

const FluxoCaixaContext = createContext();

const FluxoCaixaProvider = ({ children }) => {
  const [filters, setFilters] = useState({ ...initialFilters });
  const [groupByFinanceCategory, setGroupByFinanceCategory] = useState(false);
  const [filterPeriod, setFilterPeriod] = useState({
    ...initialFilters.searchDate,
  });
  const [isDrawerOpen, setDrawerOpen] = useState(false);

  const [financeCategoriesFilter, setFinanceCategoriesFilter] = useState({
    filtered: [],
    grouped: [],
  });
  const [orientation, setOrientation] = useState(orientationOptions.Paisagem);
  const [orientationLabel, setOrientationLabel] = useState(
    orientationLabelOptions.Paisagem
  );
  const [columns, setColumns] = useState([
    {
      label: "Empresa",
      id: "company",
    },
    {
      label: "Descrição",
      id: "description",
      format: (item) => item ?? "Sem descrição",
    },
    {
      label: "Documento",
      id: "document",
    },
    {
      label: "Data",
      id: "date",
      format: (item) => moment(item, "YYYY-MM-DD").format("DD/MM/YYYY"),
    },
    {
      label: "Competência",
      id: "jurisdiction",
    },
    {
      label: "Situação",
      id: "situation",
    },
    {
      label: "Tipo Pagto",
      id: "doccumentType",
      format: (item) => item || <>&ndash;</>,
    },
    {
      label: "Débito (R$)",
      id: "debit",
      align: "right",
      format: (item) => {
        return `${(item ?? 0).toLocaleString("pt-BR", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 5,
        })}`;
      },
    },
    {
      label: "Crédito (R$)",
      id: "credit",
      align: "right",
      format: (item) => {
        return `${(item ?? 0).toLocaleString("pt-BR", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 5,
        })}`;
      },
    },
    {
      label: "Saldo (R$)",
      id: "balance",
      align: "right",
      format: (item) => {
        return `${(item ?? 0).toLocaleString("pt-BR", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 5,
        })}`;
      },
    },
  ]);

  const [fetchError, setFetchError] = useState("");
  const [cachedPeriodResult, setCachedPeriodResult] = useState({});
  const [cachedMovimentData, setCachedMovimentData] = useState([]);
  const [cachedTotalsData, setCachedTotalsData] = useState({});
  const [cachedFinanceCategories, setCachedFinanceCategories] = useState([]);
  const [cachedCompanies, setCachedCompanies] = useState({});
  const [cachedPreviousBalance, setCachedPreviousBalance] = useState({});

  const setFinanceFilter = useCallback((newValue) => {
    setFinanceCategoriesFilter((prev) => ({
      ...prev,
      ...newValue,
    }));
    const filteredFinanceCategoriesString = newValue?.filtered
      .map((category) => category.id)
      .join(",");
    setFilters((prev) => ({
      ...prev,
      financeCategories: filteredFinanceCategoriesString,
    }));
  }, []);

  const addBusinessPlanColumn = useCallback(
    (showFinancesCategory) => {
      const hasBusinessPlan = columns.some(
        (item) => item.id === "businessPlan"
      );

      if (showFinancesCategory && !hasBusinessPlan) {
        columns.splice(2, 0, {
          label: "Categoria",
          id: "businessPlan",
        });
      }

      if (!showFinancesCategory && hasBusinessPlan) {
        columns.splice(2, 1);
      }
    },
    [columns]
  );

  const applyFinanceFilter = useCallback(
    (showFinancesCategory, financeCategories = cachedFinanceCategories) => {
      const filtered = financeCategories.filter((item) => item.checked);
      const grouped = financeCategories.filter(
        (item) => item.checked && !item.parentId
      );
      addBusinessPlanColumn(showFinancesCategory);
      if (!showFinancesCategory) setGroupByFinanceCategory(false);

      setFinanceFilter({
        filtered,
        grouped,
      });
    },
    [addBusinessPlanColumn, cachedFinanceCategories, setFinanceFilter]
  );

  const setShowPreviousBalance = useCallback((newValue) => {
    setFilters((prev) => ({
      ...prev,
      showPreviousBalance: newValue,
    }));
  }, []);

  const setShowNotPayedAccounts = useCallback((newValue) => {
    setFilters((prev) => ({
      ...prev,
      showNotPayedAccounts: newValue,
    }));
  }, []);

  const setShowNotReceivedTitles = useCallback((newValue) => {
    setFilters((prev) => ({
      ...prev,
      showNotReceivedTitles: newValue,
    }));
  }, []);

  const setCustomDatePeriod = useCallback((newValue) => {
    setFilterPeriod((prev) => {
      return {
        ...prev,
        ...newValue,
      };
    });
  }, []);

  const setPeriod = useCallback(
    (newValue) => {
      if (newValue === periods.Personalizado) {
        setCustomDatePeriod({
          startDate: undefined,
          endDate: undefined,
        });
      }

      setFilters((prev) => ({
        ...prev,
        period: newValue,
      }));
    },
    [setCustomDatePeriod]
  );

  const setSearchDate = useCallback((newValue) => {
    setFilters((prev) => ({
      ...prev,
      startDate: newValue?.startDate
        ? moment(newValue?.startDate).format("YYYY-MM-DD")
        : moment().startOf("month").format("YYYY-MM-DD"),
      endDate: newValue?.endDate
        ? moment(newValue?.endDate).format("YYYY-MM-DD")
        : moment().endOf("month").format("YYYY-MM-DD"),
    }));
  }, []);

  const setMoviment = useCallback((newValue) => {
    setFilters((prev) => ({
      ...prev,
      moviment: newValue,
    }));
  }, []);

  const setSelectedCompany = useCallback((newValue) => {
    setFilters((prev) => ({
      ...prev,
      multicompany: newValue,
    }));
  }, []);

  const setReportType = useCallback((newValue) => {
    if (newValue === reportTypes.Detalhado) {
      setOrientation(orientationOptions.Paisagem);
      setOrientationLabel(orientationLabelOptions.Paisagem);
    } else {
      setOrientation(orientationOptions.Retrato);
      setOrientationLabel(orientationLabelOptions.Retrato);
    }
    setFilters((prev) => ({
      ...prev,
      reportType: newValue,
    }));
  }, []);

  const setGroupBy = useCallback((newValue) => {
    setFilters((prev) => ({
      ...prev,
      group: newValue,
    }));
  }, []);

  const setFinanceCategories = useCallback(
    (newValue) => {
      setFilters((prev) => ({
        ...prev,
        useFinanceCategories: newValue,
      }));
      if (!newValue) {
        const allUncheckedCategories = cachedFinanceCategories.map((item) => ({
          ...item,
          checked: false,
        }));
        setGroupByFinanceCategory(false);
        setCachedFinanceCategories(allUncheckedCategories);
      }
    },
    [cachedFinanceCategories]
  );

  const applyPeriod = useCallback(
    (periodDates) => {
      setCachedPeriodResult(periodDates);
      setCustomDatePeriod({
        startDate: moment(periodDates.startDate, "YYYY-MM-DD").toDate(),
        endDate: moment(periodDates.endDate, "YYYY-MM-DD").toDate(),
      });
    },
    [setCustomDatePeriod]
  );

  useEffect(() => {
    setSearchDate(filterPeriod);
  }, [filterPeriod, setSearchDate]);
  //-----------------------------------

  const getOptionsToList = (objectOption) => {
    const options = Object.keys(objectOption).map((item) => ({
      description: item,
      value: objectOption[item],
    }));
    return options;
  };

  //-----------------------------------

  const fetchCashFlow = useCallback(
    async (res) => {
      setFetchError(null);
      try {
        const data = res?.data;

        if (!res?.success) {
          if (res.statusCode === 401) {
            throw new Error("Token expirado");
          }
          throw new Error(res?.mensagem || res?.message || "Algo deu errado");
        }
        applyPeriod(data?.searchPeriod);
        setCachedMovimentData(data?.movements || []);
        setCachedTotalsData(data?.totals || {});
        setCachedPreviousBalance(data?.previousBalance || {});
      } catch (error) {
        setFetchError(error.message);
        notify(error.message, true, "error");
      }
    },
    [applyPeriod]
  );

  const {
    data: cashData,
    isLoading: cashLoading,
    refetch,
    isRefetching,
  } = useQuery("cashFlow", async () => await findCashFlow(filters), {
    enabled: !!sessionStorage.getItem("gfAccessToken"),
  });

  const refreshCashFlow = useCallback(async () => await refetch(), [refetch]);

  useEffect(() => {
    const data = cashData?.data ?? [];
    const hasError = data?.message;
    if (!cashLoading && cashData?.data && !hasError) {
      fetchCashFlow(cashData);
    }
  }, [cashData, cashLoading, fetchCashFlow]);

  const clearAllFinanceFilter = useCallback(() => {
    const newList = cachedFinanceCategories.map((item) => ({
      ...item,
      checked: false,
    }));

    setCachedFinanceCategories(newList);
    setFinanceCategories(false);
    setFinanceFilter({
      filtered: [],
      grouped: [],
    });
  }, [cachedFinanceCategories, setFinanceCategories, setFinanceFilter]);

  //---------------------------------------------------------------------------------

  const { data: companies, isLoading: loadingCompanies } = useQuery(
    "companies",
    async () => await findCompaniesV3(),
    {
      enabled: !!sessionStorage.getItem("gfAccessToken"),
    }
  );

  useEffect(() => {
    const data = companies?.data ?? [];
    const hasError = data?.message;
    if (!loadingCompanies && companies?.data && !hasError) {
      const allCompaniesString = data.map((company) => company.id).join(",");
      const formatedCompanies = {
        Todos: allCompaniesString,
      };
      data.forEach(
        (company) => (formatedCompanies[company.nomeempresa] = company.id)
      );
      setCachedCompanies(formatedCompanies);
    }
  }, [companies, loadingCompanies, setSelectedCompany]);

  const { data: costCenter, isLoading: loadingCostCenter } = useQuery(
    "costCenter",
    async () =>
      await getCentroCusto({
        empresa_cnpj: sessionStorage.getItem("empresa_cnpj"),
        per_page: 200,
      }),
    { enabled: !!sessionStorage.getItem("empresa_cnpj") }
  );

  useEffect(() => {
    const data = costCenter?.data ?? [];
    if (data.length) {
      const financeCategories = data
        .sort((a, b) => {
          const codeA = +a.codigo;
          const codeB = +b.codigo;

          if (codeA < codeB) {
            return -1;
          }
          if (codeA > codeB) {
            return 1;
          }
          return 0;
        })
        .map((category) => ({
          id: category.id,
          desc: `${category.codigo} - ${category.descricao}`,
          checked: false,
          parentId: category.centrocustopai_id,
        }));
      setCachedFinanceCategories(financeCategories);
    }
  }, [costCenter?.data]);

  const contextValue = useMemo(
    () => ({
      financeCategoriesFilter,
      orientation,
      setOrientation,
      orientationLabel,
      setOrientationLabel,
      cachedFinanceCategories,
      setCachedFinanceCategories,
      cashLoading,
      columns,
      setColumns,
      cachedPeriodResult,
      filters,
      filterPeriod,
      groupByFinanceCategory,
      setFilterPeriod,
      setPeriod,
      setCustomDatePeriod,
      setMoviment,
      setSelectedCompany,
      setReportType,
      setGroupBy,
      setShowPreviousBalance,
      setGroupByFinanceCategory,
      setFinanceCategories,
      getOptionsToList,
      setFinanceFilter,
      applyFinanceFilter,
      clearAllFinanceFilter,
      cachedMovimentData,
      cachedTotalsData,
      fetchCashFlow,
      fetchError,
      cachedCompanies,
      loadingCompanies,
      isDrawerOpen,
      setDrawerOpen,
      cachedPreviousBalance,
      setCachedPreviousBalance,
      setShowNotPayedAccounts,
      setShowNotReceivedTitles,
      loadingCostCenter,
      refetch,
      refreshCashFlow,
      isRefetching,
    }),
    [
      financeCategoriesFilter,
      orientation,
      setOrientation,
      orientationLabel,
      setOrientationLabel,
      cachedFinanceCategories,
      cashLoading,
      columns,
      cachedPeriodResult,
      filters,
      filterPeriod,
      groupByFinanceCategory,
      setPeriod,
      setCustomDatePeriod,
      setMoviment,
      setSelectedCompany,
      setReportType,
      setGroupBy,
      setShowPreviousBalance,
      setFinanceCategories,
      setFinanceFilter,
      applyFinanceFilter,
      clearAllFinanceFilter,
      cachedMovimentData,
      cachedTotalsData,
      fetchCashFlow,
      fetchError,
      cachedCompanies,
      loadingCompanies,
      isDrawerOpen,
      cachedPreviousBalance,
      setShowNotPayedAccounts,
      setShowNotReceivedTitles,
      loadingCostCenter,
      refetch,
      refreshCashFlow,
      isRefetching,
    ]
  );

  return (
    <FluxoCaixaContext.Provider value={contextValue}>
      {children}
    </FluxoCaixaContext.Provider>
  );
};

const useFluxoDeCaixaService = () => {
  const context = useContext(FluxoCaixaContext);

  if (!context) {
    throw new Error(
      "useFluxoDeCaixaService must be used with a FluxoCaixaProvider"
    );
  }

  return context;
};

export { useFluxoDeCaixaService, FluxoCaixaProvider };
